import { PanelLeft } from "lucide-react";
import { Building2, FolderKanban } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { useProjects } from "@/contexts/ProjectContext";
import { useOrganization } from "@/contexts/OrganizationContext";

/**
 * The project chip is a summary, not the switcher.
 *
 * The old layout rendered the full ProjectSwitcher (1,000 lines) inline in the
 * sidebar. In the new design it moves to the top bar as a compact chip, so this
 * shows the current selection and leaves opening the switcher to a follow-up —
 * ProjectSwitcher still depends on react-router hooks and cannot be rendered
 * inside a TanStack route until that is untangled.
 *
 * Reading the selection here is safe either way: ProjectProvider sits ABOVE
 * RouterProvider in App.jsx, so both router trees share one source of truth.
 */
const ProjectChip = () => {
  const { t } = useTranslation();
  const { selectedProjects, availableProjects, getProjectName } = useProjects();

  const isAll =
    !selectedProjects ||
    selectedProjects === "all" ||
    (Array.isArray(selectedProjects) && selectedProjects.length === 0);

  const label = isAll
    ? t("sidebar.projectsCount", { count: availableProjects?.length ?? 0 })
    : Array.isArray(selectedProjects) && selectedProjects.length === 1
      ? (getProjectName?.(selectedProjects[0]) ?? String(selectedProjects[0]))
      : t("sidebar.projectsCount", {
          count: Array.isArray(selectedProjects) ? selectedProjects.length : 0,
        });

  return (
    <span className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-sm text-card-foreground">
      <FolderKanban className="size-4 shrink-0 opacity-70" aria-hidden="true" />
      <span className="max-w-[160px] truncate">{label}</span>
    </span>
  );
};

const OrgChip = () => {
  const { currentOrganization } = useOrganization();
  const name = currentOrganization?.name;
  if (!name) return null;

  return (
    <span className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-sm text-card-foreground">
      <Building2 className="size-4 shrink-0 opacity-70" aria-hidden="true" />
      <span className="max-w-[160px] truncate">{name}</span>
    </span>
  );
};

export const TopBar = ({
  title,
  collapsed,
  onToggleSidebar,
  actions,
}: {
  title?: string;
  collapsed: boolean;
  onToggleSidebar: () => void;
  actions?: React.ReactNode;
}) => {
  const { t } = useTranslation();

  return (
    <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border px-4">
      <button
        type="button"
        onClick={onToggleSidebar}
        aria-label={t("sidebar.toggle")}
        aria-expanded={!collapsed}
        className={cn(
          "rounded-md p-2 text-muted-foreground",
          "hover:bg-accent hover:text-accent-foreground",
        )}
      >
        <PanelLeft className="size-5" aria-hidden="true" />
      </button>

      {title && <h1 className="truncate text-base font-medium">{title}</h1>}

      <div className="ms-auto flex items-center gap-2">
        {actions}
        <ProjectChip />
        <OrgChip />
      </div>
    </header>
  );
};
